/**
 * Contractor profile — Workflows tab.
 *
 * The approvals running on everything that belongs to the contractor after award —
 * bills, mobilization advances, penalties, measurement books, extensions of time and
 * completion certificates — one row per item, pending ones first. Expanding a row shows
 * its task trail: each stage, who held it, what they did and when.
 *
 * Reads GET ds-wms/contractors/:id/workflows (dswms_wf_task_t). The officers act on these
 * items in the Workflow Inbox; this tab is the contractor-wise read of the same tasks.
 */
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import axiosInstance from '@/services/interceptor';
import { TableSkeleton } from '@/components/base/Skeleton';

interface WorkflowStep {
  id: number;
  stage: string;
  status: string;
  outcome: string | null;
  owner: string | null;
  ownerDesignation: string | null;
  actedBy: string | null;
  raisedAt: string | null;
  actedAt: string | null;
  nextAction: string | null;
  remarks: string | null;
}

interface WorkflowItem {
  key: string;
  entityType: string;
  entityId: number;
  workflowName: string;
  reference: string;
  subject: string;
  workId: number | null;
  workCode: string;
  workName: string;
  currentStage: string;
  isPending: boolean;
  state: string;
  pendingWith: string | null;
  pendingWithDesignation: string | null;
  startedAt: string | null;
  lastUpdatedAt: string | null;
  steps: WorkflowStep[];
}

const ENTITY_META: Record<string, { label: string; icon: string }> = {
  BILL: { label: 'Bill', icon: 'ri-bill-line' },
  MOBILIZATION_ADVANCE: { label: 'Mobilization Advance', icon: 'ri-hand-coin-line' },
  PENALTY: { label: 'Penalty', icon: 'ri-error-warning-line' },
  MEASUREMENT: { label: 'Measurement Book', icon: 'ri-ruler-2-line' },
  EOT: { label: 'Extension of Time', icon: 'ri-time-line' },
  COMPLETION_CERTIFICATE: { label: 'Completion Certificate', icon: 'ri-award-line' },
};

/** SUBMITTED -> Submitted, CHECK_MEASURED -> Check Measured. */
const titleCase = (code: string | null | undefined) =>
  (code || '').toLowerCase().split('_').filter(Boolean).map((w) => w[0].toUpperCase() + w.slice(1)).join(' ');

const fmtDateTime = (v: string | null) =>
  (v ? new Date(v).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-');

const stateTone = (it: WorkflowItem) => {
  if (it.isPending) return 'bg-amber-50 text-amber-700';
  if (/REJECT|RETURN|CANCEL/.test(it.state)) return 'bg-red-50 text-red-700';
  return 'bg-green-50 text-green-700';
};

interface Props {
  contractorId: number;
  onWorkIdClick: (workId: string) => void;
}

export default function ContractorWorkflowsTab({ contractorId, onWorkIdClick }: Props) {
  const [items, setItems] = useState<WorkflowItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'pending' | 'completed'>('all');

  const load = useCallback(async () => {
    if (!contractorId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axiosInstance.get(`ds-wms/contractors/${contractorId}/workflows`);
      setItems(res.data?.data || []);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to load workflows');
    } finally {
      setLoading(false);
    }
  }, [contractorId]);

  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => ({
    all: items.length,
    pending: items.filter((i) => i.isPending).length,
    completed: items.filter((i) => !i.isPending).length,
  }), [items]);

  const shown = useMemo(() => items.filter((i) =>
    filter === 'all' ? true : filter === 'pending' ? i.isPending : !i.isPending), [items, filter]);

  if (loading) return <div className="p-4"><TableSkeleton rows={4} columns={6} showToolbar={false} /></div>;

  if (error) {
    return (
      <div className="p-8 text-center text-sm text-red-600">
        <i className="ri-error-warning-line mr-1"></i>{error}
        <button onClick={load} className="ml-3 text-blue-600 hover:underline">Retry</button>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="flex flex-1 items-center justify-center p-8">
        <div className="text-center">
          <div className="mx-auto mb-3 flex h-16 w-16 items-center justify-center rounded-2xl bg-gray-100">
            <i className="ri-flow-chart text-3xl text-gray-400"></i>
          </div>
          <h3 className="text-sm font-semibold text-gray-700">No workflows yet</h3>
          <p className="mt-1 text-xs text-gray-500">
            Approvals on this contractor's bills, advances, measurements, penalties, extensions and completion certificates will appear here.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3 p-4">
      <div className="flex flex-wrap items-center gap-2">
        {(['all', 'pending', 'completed'] as const).map((k) => (
          <button
            key={k}
            onClick={() => setFilter(k)}
            className={`rounded-full border px-3 py-1 text-xs font-medium transition ${
              filter === k ? 'border-teal-600 bg-teal-600 text-white' : 'border-gray-200 bg-white text-gray-600 hover:border-teal-300'
            }`}
          >
            {k === 'all' ? 'All' : k === 'pending' ? 'Pending' : 'Completed'} ({counts[k]})
          </button>
        ))}
      </div>

      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <table className="w-full text-xs">
          <thead className="bg-gray-50 text-[10px] uppercase tracking-wide text-gray-500">
            <tr>
              <th className="w-8 px-2 py-2"></th>
              <th className="px-3 py-2 text-left">Item</th>
              <th className="px-3 py-2 text-left">Reference</th>
              <th className="px-3 py-2 text-left">Work</th>
              <th className="px-3 py-2 text-left">Current Stage</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-left">Pending With</th>
              <th className="px-3 py-2 text-left">Last Updated</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {shown.length === 0 && (
              <tr><td colSpan={8} className="px-3 py-6 text-center text-gray-400">Nothing {filter} for this contractor.</td></tr>
            )}
            {shown.map((it) => {
              const meta = ENTITY_META[it.entityType] || { label: titleCase(it.entityType), icon: 'ri-flow-chart' };
              const open = expanded === it.key;
              return (
                <Fragment key={it.key}>
                  <tr className="cursor-pointer hover:bg-gray-50" onClick={() => setExpanded(open ? null : it.key)}>
                    <td className="px-2 py-2 text-center text-gray-400">
                      <i className={`ri-arrow-right-s-line inline-block text-base transition-transform ${open ? 'rotate-90' : ''}`}></i>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1.5 font-medium text-gray-800">
                        <i className={`${meta.icon} text-teal-600`}></i>{meta.label}
                      </div>
                    </td>
                    <td className="px-3 py-2 font-mono text-[11px] text-gray-700" title={it.subject}>{it.reference || '-'}</td>
                    <td className="max-w-[200px] px-3 py-2">
                      {it.workId ? (
                        <button
                          onClick={(e) => { e.stopPropagation(); onWorkIdClick(String(it.workId)); }}
                          className="block max-w-full truncate text-left text-blue-600 hover:underline"
                          title={it.workName}
                        >
                          {it.workCode || it.workName}
                        </button>
                      ) : <span className="text-gray-400">-</span>}
                    </td>
                    <td className="px-3 py-2 text-gray-700">{titleCase(it.currentStage) || '-'}</td>
                    <td className="px-3 py-2">
                      <span className={`rounded px-2 py-0.5 text-[10px] font-semibold ${stateTone(it)}`}>
                        {it.isPending ? 'Pending' : titleCase(it.state)}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-gray-700">
                      {it.isPending ? (
                        <>
                          {it.pendingWith || '-'}
                          {it.pendingWithDesignation && it.pendingWithDesignation !== it.pendingWith && (
                            <div className="text-[10px] text-gray-400">{it.pendingWithDesignation}</div>
                          )}
                        </>
                      ) : <span className="text-gray-400">-</span>}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-gray-600">{fmtDateTime(it.lastUpdatedAt)}</td>
                  </tr>

                  {open && (
                    <tr className="bg-slate-50/70">
                      <td></td>
                      <td colSpan={7} className="px-3 py-3">
                        {it.subject && <div className="mb-2 text-[11px] text-gray-600">{it.subject}</div>}
                        <ol className="relative ml-1 space-y-2 border-l border-gray-200 pl-4">
                          {it.steps.map((s) => {
                            const pending = s.status === 'PENDING';
                            return (
                              <li key={s.id} className="relative">
                                <span className={`absolute -left-[21px] top-1 h-2.5 w-2.5 rounded-full ring-2 ring-white ${
                                  pending ? 'bg-amber-400' : /REJECT|RETURN|CANCEL/.test(s.outcome || '') ? 'bg-red-500' : 'bg-green-500'
                                }`}></span>
                                <div className="flex flex-wrap items-baseline gap-x-2 text-[11px]">
                                  <span className="font-semibold text-gray-800">{titleCase(s.stage)}</span>
                                  <span className={pending ? 'text-amber-700' : 'text-gray-500'}>
                                    {pending ? 'Waiting' : titleCase(s.outcome)}
                                  </span>
                                  <span className="text-gray-400">
                                    {pending
                                      ? `with ${s.owner || s.ownerDesignation || 'unassigned'} · since ${fmtDateTime(s.raisedAt)}`
                                      : `by ${s.actedBy || s.owner || '-'} · ${fmtDateTime(s.actedAt || s.raisedAt)}`}
                                  </span>
                                </div>
                                {(s.remarks || (pending && s.nextAction)) && (
                                  <div className="mt-0.5 text-[11px] text-gray-500">
                                    {s.remarks && <span><i className="ri-chat-quote-line mr-0.5"></i>{s.remarks}</span>}
                                    {pending && s.nextAction && <span className="ml-2 text-amber-700">Next: {s.nextAction}</span>}
                                  </div>
                                )}
                              </li>
                            );
                          })}
                        </ol>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
